import { i18nTranslationHelper } from '@/i18n'
import {
  makeSkillWebMyProfileRedirectUrl,
  makeSkillWebMyProfileRefundsRedirectUrl
} from '@/main/factories/http'
import {
  computeContentValueIdHelper,
  getCategoriesPathsHelper,
  getCategoryGroupPathsHelper
} from '../../shared/path-utils'

const t = i18nTranslationHelper.getTPage

// ============================================
// MÓDULO USER — ROTAS AUTENTICADAS
// Fluxos normais da aplicação para perfis colaborador / dependente:
// home, descoberta de conteúdo, onboarding, perfil, inscrições, solicitações, etc.
// ============================================

export const USER_ROUTES = {
  INDEX: {
    value: '/',
    name: 'home',
    title: t('home.pageTitle')
  },
  HOME: {
    value: '/',
    name: 'home',
    title: t('home.pageTitle')
  },
  SEARCH: {
    value: '/search',
    name: 'search',
    title: 'Busca'
  },
  SKILLS: {
    value: '/skills/:id',
    name: 'skill',
    title: 'Habilidades',
    computeValue: (args: { id: string | number }) => `/skills/${args.id}`
  },
  INSTITUTIONS: {
    value: '/partners',
    name: 'partners',
    title: t('partners.pageTitle')
  },
  INSTITUTION: {
    value: '/partners/:id',
    name: 'partner',
    computeValue: (args: { id: string | number }) => `/partners/${args.id}`
  },
  CONTENT: {
    value: '/content/:id',
    name: 'content',
    computeValue: ({ content }: { content: { id: number; productId: number } }) =>
      `/content/${computeContentValueIdHelper(content)}`
  },
  ...getCategoriesPathsHelper(),
  ...getCategoryGroupPathsHelper(),

  // Ações de Conteúdo
  CONTENT_ENROLLMENT: {
    value: '/content/:id/enrollment',
    name: 'content_enrollment',
    computeValue: (id: string | number) => `/content/${id}/enrollment`
  },
  CONTENT_ENROLLMENT_SCHOLARSHIP_INFO: {
    value: '/enrollment/scholarship-info',
    name: 'content_enrollment_scholarship_info'
  },
  CONTENT_UNSUBSCRIBE: {
    value: '/content/enrollment/:id/unsubscribe',
    name: 'content_unsubscribe',
    computeValue: (args: { id: string | number }) => `/content/enrollment/${args.id}/unsubscribe`
  },
  CONTENT_COMPLETE: {
    value: '/content/enrollment/:id/complete',
    name: 'content_complete',
    computeValue: (args: { id: string | number }) => `/content/enrollment/${args.id}/complete`
  },
  CONTENT_FIT_SCORE: {
    // O param `:id` aqui é o productId (mesmo padrão da rota CONTENT). O loader chama
    // loadContentDetails(params.id) que espera productId.
    value: '/content/:id/fit-score',
    name: 'content_fit_score',
    title: t('contentFitScoreRecommendations.pageTitle'),
    computeValue: ({ productId }: { productId: number }) => `/content/${productId}/fit-score`
  },

  // Onboarding
  ONBOARDING: {
    value: '/onboarding',
    name: 'onboarding'
  },
  ONBOARDING_CHECK: {
    value: '/onboarding/check',
    name: 'onboarding_check'
  },
  ONBOARDING_1: {
    value: '/onboarding/1',
    name: 'onboarding_1'
  },
  ONBOARDING_PERSONAL_DATA: {
    value: '/onboarding/personal-data',
    name: 'onboarding_personal_data'
  },
  ONBOARDING_PROFESSIONAL_TRAJECTORY: {
    value: '/onboarding/professional-trajectory',
    name: 'professional_trajectory'
  },
  ONBOARDING_CAREER_MOMENT: {
    value: '/onboarding/career-moment',
    name: 'onboarding_career_moment'
  },
  ONBOARDING_LEARNING_OBJECTIVES: {
    value: '/onboarding/learning-objectives',
    name: 'onboarding_learning_objectives'
  },
  ONBOARDING_CONTENT_TYPES: {
    value: '/onboarding/content-types',
    name: 'onboarding_content_types'
  },
  ONBOARDING_CONTENT_FORMATS: {
    value: '/onboarding/content-formats',
    name: 'onboarding_content_formats'
  },
  ONBOARDING_3: {
    value: '/onboarding/3',
    name: 'onboarding_3'
  },
  ONBOARDING_4: {
    value: '/onboarding/4',
    name: 'onboarding_4'
  },
  ONBOARDING_SECURITY: {
    value: '/onboarding/security',
    name: 'onboarding_security'
  },
  ONBOARDING_COMPLETED: {
    value: '/onboarding/completed',
    name: 'onboarding_completed'
  },

  // Perfil & Configurações do Usuário
  USER_PROFILE: {
    value: '/user/profile',
    name: 'user_profile',
    title: t('userProfile.pageTitle')
  },
  USER_INSTEREST_AREAS_SKILLS: {
    value: '/user/interest-areas-skills',
    name: 'user_interest_areas_skills',
    title: t('userInterestAreasSkills.pageTitle')
  },
  USER_SETTINGS: {
    value: '/user/settings',
    name: 'user_settings',
    title: t('userSettings.pageTitle')
  },
  USER_ENROLLMENTS: {
    value: makeSkillWebMyProfileRedirectUrl(),
    name: 'monolith_user_profile',
    title: t('userEnrollments.pageTitle')
  },
  USER_ENROLLMENTS_REFUNDS: {
    value: makeSkillWebMyProfileRefundsRedirectUrl(),
    name: 'monolith_user_profile_refunds',
    title: t('userEnrollments.pageTitle')
  },
  USER_SPACE: {
    value: '/user/space',
    name: 'user_space',
    title: t('userSpace.pageTitle')
  },
  USER_ENROLLMENTS_V2: {
    value: '/user/enrollments',
    name: 'user_enrollments',
    title: t('userEnrollments.pageTitle')
  },
  USER_ENROLLMENT_DETAIL: {
    value: '/user/enrollments/:enrollmentId',
    name: 'user_enrollment_detail',
    title: t('userEnrollmentDetail.pageTitle'),
    computeValue: ({ enrollmentId }: { enrollmentId: string | number }) =>
      `/user/enrollments/${enrollmentId}`,
    computeTitle: ({ enrollmentId }: { enrollmentId: string | number }) =>
      `${t('userEnrollmentDetail.pageTitle')}${enrollmentId}`
  },
  USER_ENROLLMENT_ENGAGEMENT: {
    value: '/user/enrollments/:enrollmentId/engagement',
    name: 'user_enrollment_engagement',
    title: t('userEnrollmentEngagement.pageTitle'),
    computeValue: ({ enrollmentId }: { enrollmentId: string | number }) =>
      `/user/enrollments/${enrollmentId}/engagement`
  },
  USER_ENROLLMENT_SCHOLARSHIP_RENEWAL: {
    value: '/user/enrollments/:enrollmentId/scholarship-renewal',
    name: 'user_enrollment_scholarship_renewal',
    title: t('userEnrollmentDetail.userEnrollmentScholarshipRenewal.pageTitle'),
    computeValue: ({ enrollmentId }: { enrollmentId: string | number }) =>
      `/user/enrollments/${enrollmentId}/scholarship-renewal`
  },
  FAVORITES: {
    value: '/user/favorites',
    name: 'favorites',
    title: t('favorites.pageTitle')
  },

  // CB Único & Avaliação de Perfil
  CBU_START: {
    value: '/cbu/start',
    name: 'cbu_start'
  },
  PROFILE_ASSESSMENT_CALLBACK: {
    value: '/assessment/callback',
    name: 'profile_assessment_callback'
  },
  PROFILE_ASSESSMENT_IN_PROGRESS: {
    value: '/assessment/progress',
    name: 'profile_assessment_in_progress'
  },
  PROFILE_ASSESSMENT_GET_STARTED: {
    value: '/assessment/get-started',
    name: 'profile_assessment_get_started'
  },

  // Reembolsos
  REFUND: {
    value: '/refund',
    name: 'refund_program_list',
    title: t('refund.title')
  },
  REFUND_REQUEST: {
    value: '/refund/program/:id/request',
    name: 'refund_request',
    computeValue: (args: { id: string | number }) => `/refund/program/${args.id}/request`
  },

  // Solicitações de Upgrade
  UPGRADE_REQUEST: {
    value: '/upgrade-request',
    name: 'upgrade_request'
  },

  // Solicitações de Dependente
  DEPENDENT_REQUEST: {
    value: '/dependent-request',
    name: 'dependent_request'
  },
  DEPENDENT_REQUEST_CATALOG: {
    value: '/dependent-request/catalog',
    name: 'dependent_request_catalog',
    title: t('dependentRequest.catalog.pageTitle')
  },
  DEPENDENT_REQUEST_CONTENT: {
    value: '/dependent-request/catalog/:id',
    name: 'dependent_request_content',
    computeValue: ({ content }: { content: { id: number; productId: number } }) =>
      `/dependent-request/catalog/${computeContentValueIdHelper(content)}`
  },

  // Verificação de Contato
  CONTACT_VERIFICATION: {
    value: '/contact-verification',
    name: 'contact_verification',
    title: t('contactVerification.title')
  },

  // Sem Plano
  NO_PLAN: {
    value: '/no-plan',
    name: 'no_plan'
  }
} as const
